import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { Link, useNavigate } from 'react-router-dom'
import toast, { Toaster } from 'react-hot-toast'
import { FiPlus, FiTrash2, FiAlertTriangle, FiChevronLeft, FiChevronRight, FiInbox } from 'react-icons/fi'
import '../../styles/AdminTable.css'

const ITEMS_PER_PAGE = 10
const API = process.env.REACT_APP_API_URL

const TableActualite = () => {
  const authorization = localStorage.getItem('token')
  const headers = { Authorization: `Bearer ${authorization}` }
  const navigate = useNavigate()

  const [infoac, setInfoac] = useState([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState([])
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting] = useState(false)

  const handleAuthError = (err) => {
    if ([401, 403, 404].includes(err.response?.status)) {
      localStorage.clear()
      navigate('/login')
    }
  }

  const fetchData = () => {
    setLoading(true)
    axios.get(`${API}/actualite/lister`)
      .then((res) => setInfoac(res.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { fetchData() }, [])

  const nbPages = Math.max(1, Math.ceil(infoac.length / ITEMS_PER_PAGE))
  const pageData = useMemo(() => infoac.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE), [infoac, page])

  useEffect(() => { setPage(1) }, [infoac.length])

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))
  const toggleSelectAll = () => setSelected(selected.length === pageData.length ? [] : pageData.map((t) => t._id))

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await axios.get(`${API}/actualite/${deleteTarget._id}/delete`, { headers })
      setInfoac((t) => t.filter((x) => x._id !== deleteTarget._id))
      setSelected((s) => s.filter((x) => x !== deleteTarget._id))
      toast.success('Fichier supprimé')
      setDeleteTarget(null)
    } catch (err) {
      toast.error('Échec de la suppression')
      handleAuthError(err)
    } finally {
      setDeleting(false)
    }
  }

  const handleBulkDelete = async () => {
    if (!selected.length) return
    if (!window.confirm(`Supprimer ${selected.length} fichier(s) sélectionné(s) ?`)) return
    try {
      await Promise.all(selected.map((id) => axios.get(`${API}/actualite/${id}/delete`, { headers })))
      setInfoac((t) => t.filter((x) => !selected.includes(x._id)))
      setSelected([])
      toast.success('Fichiers supprimés')
    } catch (err) {
      toast.error('Échec de la suppression groupée')
      handleAuthError(err)
    }
  }

  return (
    <div className="atl-page">
      <Toaster />

      <div className="atl-header">
        <div>
          <h1 className="atl-title">Actualités</h1>
          <p className="atl-subtitle">{loading ? 'Chargement…' : `${infoac.length} fichier${infoac.length > 1 ? 's' : ''}`}</p>
        </div>
        <Link to="/admin/Actualite/ajouter" className="atl-btn-add">
          <FiPlus size={16} />
          Ajouter
        </Link>
      </div>

      <div className="atl-toolbar">
        <span className="atl-subtitle">{loading ? '' : `${infoac.length} fichier${infoac.length > 1 ? 's' : ''} au total`}</span>

        {selected.length > 0 && (
          <button className="atl-bulk-delete" onClick={handleBulkDelete}>
            <FiTrash2 size={14} />
            Supprimer ({selected.length})
          </button>
        )}
      </div>

      <div className="atl-table-wrap">
        <table className="atl-table">
          <thead>
            <tr>
              <th className="atl-th-check">
                <input type="checkbox" checked={pageData.length > 0 && selected.length === pageData.length} onChange={toggleSelectAll} />
              </th>
              <th>Vidéo</th>
              <th className="atl-th-actions">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={i} className="atl-skeleton-row"><td colSpan={3}><div className="atl-skeleton" /></td></tr>
              ))
            ) : pageData.length === 0 ? (
              <tr>
                <td colSpan={3} className="atl-empty">
                  <FiInbox size={36} />
                  <p>Aucune actualité pour le moment</p>
                </td>
              </tr>
            ) : (
              pageData.map((t) => (
                <tr key={t._id} className={selected.includes(t._id) ? 'atl-row--selected' : ''}>
                  <td className="atl-th-check">
                    <input type="checkbox" checked={selected.includes(t._id)} onChange={() => toggleSelect(t._id)} />
                  </td>
                  <td>
                    <video
                      width="140"
                      controls
                      style={{ borderRadius: '8px', border: '1px solid rgba(255, 255, 255, 0.08)' }}
                    >
                      <source src={t.image} type="video/mp4" />
                    </video>
                  </td>
                  <td className="atl-th-actions">
                    <div className="atl-actions">
                      <button className="atl-action-btn atl-action-btn--delete" title="Supprimer" onClick={() => setDeleteTarget(t)}>
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!loading && nbPages > 1 && (
        <div className="atl-pagination">
          <button className="atl-page-btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)}><FiChevronLeft /></button>
          {Array.from({ length: nbPages }, (_, i) => i + 1).map((p) => (
            <button key={p} className={`atl-page-btn ${p === page ? 'atl-page-btn--active' : ''}`} onClick={() => setPage(p)}>{p}</button>
          ))}
          <button className="atl-page-btn" disabled={page === nbPages} onClick={() => setPage((p) => p + 1)}><FiChevronRight /></button>
        </div>
      )}

      {deleteTarget && (
        <div className="atl-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="atl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="atl-modal__icon"><FiAlertTriangle size={22} /></div>
            <h3 className="atl-modal__title">Supprimer ce fichier ?</h3>
            <p className="atl-modal__text">Ce fichier sera supprimé définitivement. Cette action est irréversible.</p>
            <div className="atl-modal__actions">
              <button className="atl-modal__btn atl-modal__btn--cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>Annuler</button>
              <button className="atl-modal__btn atl-modal__btn--confirm" onClick={handleDelete} disabled={deleting}>{deleting ? 'Suppression…' : 'Supprimer'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default TableActualite
